import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Filter, X, ChevronLeft, Calendar, Check,
  ArrowUpDown, ArrowUp, ArrowDown, RotateCcw, Loader2, AlertCircle, ChevronRight
} from "lucide-react";
import MainLayout from "./MainLayout";
import "../styles/leaveHistory.css";
import { useAuth } from "../context/AuthContext";
import { getLeavesByEmployee, cancelLeaveRequest } from "../services/leaveApiService";

const DAY_RANGES = ["All", "< 1 day", "1 day", "2–3 days", "4–7 days", "7+ days"];

const STATUS_META = {
  PENDING:   { cls: "lh-badge--pending",   dot: "#F59E0B" },
  APPROVED:  { cls: "lh-badge--approved",  dot: "#10B981" },
  REJECTED:  { cls: "lh-badge--rejected",  dot: "#EF4444" },
  CANCELLED: { cls: "lh-badge--cancelled", dot: "#9CA3AF" },
};

function fmt(dateStr) {
  if (!dateStr) return "--";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const local = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return `${String(local.getDate()).padStart(2, '0')}-${String(local.getMonth() + 1).padStart(2, '0')}-${local.getFullYear()}`;
}

function getDays(row) {
  const units = parseFloat(row.total_units || 0);
  return row.unit_type === "HOUR" ? units / 8 : units;
}

function getUnitsLabel(row) {
  const units = parseFloat(row.total_units || 0);
  return row.unit_type === "HOUR"
    ? `${units} hrs`
    : `${units} day${units !== 1 ? "s" : ""}`;
}

function matchDayRange(days, range) {
  if (range === "All")      return true;
  if (range === "< 1 day")  return days < 1;
  if (range === "1 day")    return days === 1;
  if (range === "2–3 days") return days >= 2 && days <= 3;
  if (range === "4–7 days") return days >= 4 && days <= 7;
  if (range === "7+ days")  return days > 7;
  return true;
}

function Toast({ toast }) {
  if (!toast) return null;
  const isError = toast.type === "error";
  return (
    <div className={`lh-toast ${isError ? "lh-toast--error" : "lh-toast--success"}`}>
      {isError ? <AlertCircle size={15} /> : <Check size={15} />}
      <span>{toast.msg}</span>
    </div>
  );
}

function CancelModal({ onConfirm, onCancel, loading, reason, onReasonChange }) {
  return (
    <div className="lh-modal-backdrop" onClick={onCancel}>
      <div className="lh-modal" onClick={e => e.stopPropagation()}>
        <div className="lh-modal-icon">
          <AlertCircle size={32} color="#EF4444" />
        </div>
        <h3 className="lh-modal-title">Cancel Leave Request?</h3>
        <p className="lh-modal-desc">
          This action cannot be undone. Your leave balance will be restored if the leave was approved.
        </p>
        <textarea
          className="lh-modal-textarea"
          rows={4}
          placeholder="Enter cancellation reason"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          disabled={loading}
        />
        <div className="lh-modal-footer">
          <button className="lh-modal-btn lh-modal-btn--outline" onClick={onCancel} disabled={loading}>
            Keep Leave
          </button>
          <button
            className="lh-modal-btn lh-modal-btn--danger"
            onClick={onConfirm}
            disabled={loading || !reason.trim()}
          >
            {loading ? <Loader2 size={14} className="lh-spin" /> : <X size={14} />}
            {loading ? "Cancelling…" : "Yes, Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LeaveHistoryPage({ embedded = false }) {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();

  const empId = user?.employee_id ?? user?.employeeId ?? user?.empId ?? user?.id;

  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [history,       setHistory]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [cancelTarget,  setCancelTarget]  = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelReason,  setCancelReason]  = useState("");
  const [toast,         setToast]         = useState(null);

  // ── Dynamic filter options from API ──
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([{ val: "All", label: "All" }]);
  const [statusOptions,    setStatusOptions]    = useState([{ val: "All", label: "All" }]);

  const [search,      setSearch]      = useState("");
  const [leaveType,   setLeaveType]   = useState("All");
  const [status,      setStatus]      = useState("All");
  const [dayRange,    setDayRange]    = useState("All");
  const [startFrom,   setStartFrom]   = useState("");
  const [startTo,     setStartTo]     = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [sortKey, setSortKey] = useState("start_date");
  const [sortDir, setSortDir] = useState("desc");
  const [page,    setPage]    = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const toggleSidebar = () => setSidebarOpen((p) => !p);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Fetch history + leave types + statuses ──
  const fetchHistory = useCallback(async () => {
    if (!empId) {
      setError("Employee ID not found. Please re-login.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:3030";
      const [rows, excRes, ltData, stData] = await Promise.all([
        getLeavesByEmployee(empId, token),
        fetch(`${API_BASE}/api/employee-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : []),
        fetch(`${API_BASE}/api/leave-types`, {
          headers: { Authorization: `Bearer ${token}` }  
        }).then(r => r.json()),
        fetch(`${API_BASE}/api/dropdowns/values/STATUS`, {
          headers: { Authorization: `Bearer ${token}` }  
        }).then(r => r.json()),
      ]);

      let exceptions = [];
      if (Array.isArray(excRes)) {
        exceptions = excRes.filter(r => r.employee_id === empId).map(ex => ({
          ...ex,
          isException: true,
          leave_type_name: ex.request_type,
          total_units: (new Date(ex.end_date) - new Date(ex.start_date)) / (1000 * 60 * 60 * 24) + 1,
          unit_type: "DAY"
        }));
      }

      setHistory([...rows, ...exceptions]);

      // ── Leave type options ──
      setLeaveTypeOptions([
        { val: "All", label: "All" },
        ...(Array.isArray(ltData) ? ltData : []).map(lt => ({
          val:   lt.name,
          label: `${lt.name} (${lt.code})`,
        })),
      ]);

      // ── Status options ──
      setStatusOptions([
        { val: "All", label: "All" },
        ...(Array.isArray(stData) ? stData : []).map(s => ({
          val:   s.dropdown_value_code,
          label: s.dropdown_value_name,
        })),
      ]);

    } catch (e) {
      setError(e.message || "Failed to load leave history");
    } finally {
      setLoading(false);
    }
  }, [empId, token]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const openCancelModal = (row) => {
    setCancelTarget(row);
    setCancelReason("");
  };

  const closeCancelModal = () => {
    if (cancelLoading) return;
    setCancelTarget(null);
    setCancelReason("");
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) {
      showToast("Please provide a reason for cancellation.", "error");
      return;
    }
    setCancelLoading(true);
    try {
      await cancelLeaveRequest(cancelTarget.id, empId, token, cancelReason.trim());
      setHistory((prev) =>
        prev.map((h) => h.id === cancelTarget.id ? { ...h, status: "CANCELLED" } : h)
      );
      closeCancelModal();
      showToast("Leave request cancelled successfully");
    } catch (e) {
      closeCancelModal();
      showToast(e.message || "Failed to cancel leave", "error");
    } finally {
      setCancelLoading(false);
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const resetFilters = () => {
    setSearch(""); setLeaveType("All"); setStatus("All");
    setDayRange("All"); setStartFrom(""); setStartTo(""); setPage(1);
  };

  const filtered = useMemo(() => {
    let list = history.filter((r) => {
      const q    = search.toLowerCase();
      const name = (r.leave_type_name || "").toLowerCase();
      const st   = (r.status || "").toLowerCase();
      if (q && !name.includes(q) && !st.includes(q)) return false;
      if (leaveType !== "All" && r.leave_type_name !== leaveType) return false;
      if (status !== "All" && r.status?.toUpperCase() !== status) return false;
      if (!matchDayRange(getDays(r), dayRange)) return false;
      if (startFrom && r.start_date < startFrom) return false;
      if (startTo   && r.start_date > startTo)   return false;
      return true;
    });

    list.sort((a, b) => {
      let av = a[sortKey] ?? "";
      let bv = b[sortKey] ?? "";
      if (typeof av === "string") { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1  : -1;
      return 0;
    });

    return list;
  }, [history, search, leaveType, status, dayRange, startFrom, startTo, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated  = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const activeFilterCount = [
    leaveType !== "All", status !== "All", dayRange !== "All",
    !!startFrom, !!startTo, !!search,
  ].filter(Boolean).length;

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown size={13} className="lh-sort-icon lh-sort-icon--idle" />;
    return sortDir === "asc"
      ? <ArrowUp   size={13} className="lh-sort-icon lh-sort-icon--active" />
      : <ArrowDown size={13} className="lh-sort-icon lh-sort-icon--active" />;
  };

  const content = (
    <div className="lh-wrap" style={{ padding: 0 }}>

          {error && (
            <div className="lh-error-banner">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <div className="lh-filterbar" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '16px 0', borderBottom: '1px solid #E2E8F0', marginBottom: '16px' }}>
            <div className="lh-search-wrap" style={{ flex: 'none', width: '260px', margin: 0 }}>
              <Search size={14} className="lh-search-icon" />
              <input
                className="lh-search-input"
                style={{ padding: '8px 30px 8px 32px', borderRadius: '6px' }}
                placeholder="Search leave type or status…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
              {search && (
                <button className="lh-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
                  <X size={12} />
                </button>
              )}
            </div>
            <button
              className={`lh-filter-toggle ${filtersOpen ? "lh-filter-toggle--open" : ""}`}
              onClick={() => setFiltersOpen((p) => !p)}
            >
              <Filter size={14} /> Filters
              {activeFilterCount > 0 && (
                <span className="lh-filter-badge">{activeFilterCount}</span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button className="lh-reset-btn" onClick={resetFilters}>
                <RotateCcw size={13} /> Reset
              </button>
            )}
          </div>

          {filtersOpen && (
            <>
              <div className="lh-modal-backdrop" onClick={() => setFiltersOpen(false)} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.4)', zIndex: 999 }} />
              <div style={{ position: 'fixed', top: 0, right: 0, width: '340px', height: '100vh', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.1)', zIndex: 1000, display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease' }}>
                <style>{`
                  @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
                `}</style>
                <div style={{ padding: '24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#0f172a' }}>Filters</h3>
                  <button onClick={() => setFiltersOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
                </div>
                
                <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  
                  {/* Status */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Status</label>
                    <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc' }}>
                      {statusOptions.map((opt) => (
                        <option key={opt.val} value={opt.val}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Leave Type */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Leave Type</label>
                    <select value={leaveType} onChange={e => { setLeaveType(e.target.value); setPage(1); }} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc' }}>
                      {leaveTypeOptions.map((lt) => (
                        <option key={lt.val} value={lt.val}>{lt.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Duration */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Duration</label>
                    <select value={dayRange} onChange={e => { setDayRange(e.target.value); setPage(1); }} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc' }}>
                      {DAY_RANGES.map((dr) => (
                        <option key={dr} value={dr}>{dr}</option>
                      ))}
                    </select>
                  </div>

                  {/* Date Range */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Date Range</label>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>From</label>
                        <input type="date" value={startFrom} max={startTo || undefined} onChange={e => { setStartFrom(e.target.value); setPage(1); }} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', backgroundColor: '#f8fafc' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>To</label>
                        <input type="date" value={startTo} min={startFrom || undefined} onChange={e => { setStartTo(e.target.value); setPage(1); }} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', backgroundColor: '#f8fafc' }} />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div style={{ padding: '20px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '12px' }}>
                  <button onClick={resetFilters} style={{ flex: 1, padding: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fff', border: '1px solid #fee2e2', borderRadius: '8px', gap: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#dc2626' }}>
                    <RotateCcw size={14} /> Reset
                  </button>
                  <button onClick={() => setFiltersOpen(false)} style={{ flex: 1, padding: '10px', background: '#3b82f6', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                    Apply
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="lh-table-card">
            {loading ? (
              <div className="lh-loading">
                <Loader2 size={22} className="lh-spin" />
                <span>Loading leave history…</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="lh-empty">
                <Calendar size={48} className="lh-empty-icon" />
                <p className="lh-empty-text">
                  {history.length === 0 ? "No leave requests found" : "No records match your filters"}
                </p>
                {activeFilterCount > 0 && (
                  <button className="lh-reset-btn lh-reset-btn--center" onClick={resetFilters}>
                    <RotateCcw size={13} /> Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="lh-table-scroll">
                  <table className="lh-table">
                    <thead>
                      <tr>
                        {[
                          { key: "leave_type_name", label: "Leave Type" },
                          { key: "start_date",      label: "Start Date" },
                          { key: "end_date",        label: "End Date"   },
                          { key: "created_at",      label: "Applied On" },
                          { key: "total_units",     label: "Duration"   },
                          { key: "request_type",    label: "Type"       },
                          { key: "status",          label: "Status"     },
                          { key: null,              label: "Remarks"    },
                          { key: null,              label: "Action"     },
                        ].map((col) => (
                          <th key={col.label}
                            className={col.key ? "lh-th--sortable" : ""}
                            onClick={col.key ? () => toggleSort(col.key) : undefined}
                          >
                            <span className="lh-th-inner">
                              {col.label}
                              {col.key && <SortIcon col={col.key} />}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((row, i) => {
                        const meta      = STATUS_META[row.status?.toUpperCase()] || STATUS_META.REJECTED;
                        const canCancel = row.status?.toUpperCase() === "PENDING";
                        return (
                          <tr key={row.id} className="lh-tr" style={{ animationDelay: `${i * 40}ms` }}>
                            <td className="lh-td lh-td--bold">{row.leave_type_name}</td>
                            <td className="lh-td lh-td--muted">{fmt(row.start_date)}</td>
                            <td className="lh-td lh-td--muted">{fmt(row.end_date)}</td>
                            <td className="lh-td lh-td--muted">{fmt(row.created_at)}</td>
                            <td className="lh-td lh-td--muted">{getUnitsLabel(row)}</td>
                            <td className="lh-td">
                              <div className="lh-type-cell">
                                <span className="lh-reqtype-badge">
                                  {row.isException ? "EXCEPTION" : (row.request_type || "FULL_DAY")}
                                </span>
                                {(row.start_time || row.end_time) && (
                                  <span className="lh-time-range">
                                    {row.start_time ? row.start_time.slice(0, 5) : "--"}
                                    {" → "}
                                    {row.end_time ? row.end_time.slice(0, 5) : "--"}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="lh-td">
                              <span className={`lh-status-badge ${meta.cls}`}>
                                <span className="lh-status-dot" style={{ background: meta.dot }} />
                                {row.status}
                              </span>
                            </td>

                            {/* ── Remarks ── */}
                            <td className="lh-td">
                              {row.status?.toUpperCase() === "REJECTED" && row.rejection_reason ? (
                                <span className="lh-remark lh-remark--rejected" title={row.rejection_reason}>
                                  {row.rejection_reason.length > 30
                                    ? row.rejection_reason.slice(0, 30) + "…"
                                    : row.rejection_reason}
                                </span>
                              ) : row.status?.toUpperCase() === "APPROVED" && row.approval_note ? (
                                <span className="lh-remark lh-remark--approved" title={row.approval_note}>
                                  {row.approval_note.length > 30
                                    ? row.approval_note.slice(0, 30) + "…"
                                    : row.approval_note}
                                </span>
                              ) : (
                                <span className="lh-td--muted">—</span>
                              )}
                            </td>

                            {/* ── Action ── */}
                            <td className="lh-td lh-td--center">
                              {canCancel ? (
                                <button
                                  className="lh-cancel-btn"
                                  onClick={() => openCancelModal(row)}
                                >
                                  <X size={11} /> Cancel
                                </button>
                              ) : (
                                <span className="lh-td--muted">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          
          {/* Pagination */}
          {!loading && filtered.length > 0 && (
            <div className="em-pagination">
              <div className="em-pagination__left">
                <span className="em-pagination__info">
                  Showing {Math.min((page - 1) * rowsPerPage + 1, filtered.length)}–{Math.min(page * rowsPerPage, filtered.length)} of {filtered.length}
                </span>
                <div className="em-rows-selector">
                  <span className="em-rows-label">Rows:</span>
                  <select
                    className="em-rows-select"
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                    <option value={40}>40</option>
                  </select>
                </div>
              </div>
              <div className="em-pagination__btns">
                <button className="em-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}><ChevronLeft size={15} /></button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button key={i + 1} className={`em-page-btn${page === i + 1 ? " active" : ""}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
                ))}
                <button className="em-page-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}><ChevronRight size={15} /></button>
              </div>
            </div>
          )}
        </div>
  );

  if (embedded) {
    return (
      <div className="lh-embedded-container" style={{ width: '100%', height: '100%' }}>
        {content}
        {cancelTarget && (
          <CancelModal
            onConfirm={handleCancelConfirm}
            onCancel={closeCancelModal}
            loading={cancelLoading}
            reason={cancelReason}
            onReasonChange={setCancelReason}
          />
        )}
        <Toast toast={toast} />
      </div>
    );
  }

  return (
    <>
      <MainLayout 
        sidebarOpen={sidebarOpen} 
        toggleSidebar={toggleSidebar}
        title="Leave History"
      >
        {content}
      </MainLayout>

      {cancelTarget && (
        <CancelModal
          onConfirm={handleCancelConfirm}
          onCancel={closeCancelModal}
          loading={cancelLoading}
          reason={cancelReason}
          onReasonChange={setCancelReason}
        />
      )}

      <Toast toast={toast} />
    </>
  );
}